import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactTable from '../../components/ReactTable';
import Loader from '../../components/Loader';
import Toast from '../../components/ToastNotification';
import { useGetBatchMessagesMutation } from './api/bulkUploadApi';
import { formatedDateAndTime } from '../../utils/helpers';
import {
    DELIVERY_STATUS_LABELS,
    DELIVERY_STATUS_COLORS,
    ATTACHMENT_TYPE_LABELS,
    formatFileSize
} from './utils/bulkUploadHelpers';
import { DEFAULT_PAGE_SIZE } from '../../utils/contants';
import './BulkReportPage.css';
import './BulkUploadPage.css';

const BulkReportPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [batch, setBatch] = useState(null);
    const [messages, setMessages] = useState([]);
    const [showLoader, setShowLoader] = useState(true);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: DEFAULT_PAGE_SIZE
    });

    const [getBatchMessagesApi] = useGetBatchMessagesMutation();

    useEffect(() => {
        const fetchMessages = async () => {
            setShowLoader(true);
            try {
                const response = await getBatchMessagesApi(id).unwrap();
                setBatch(response?.data?.batch ?? null);
                setMessages(response?.data?.messages ?? []);
            } catch (error) {
                Toast.error(error?.data?.message || 'Failed to load batch report.');
            } finally {
                setShowLoader(false);
            }
        };

        fetchMessages();
    }, [id]);

    const handleExportCSV = () => {
        const exportUrl = `${process.env.REACT_APP_API_URL}bulk-upload/${id}/export`;
        const a = document.createElement('a');
        a.href = exportUrl;
        a.download = `export_${batch?.file_name ?? id}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const columns = useMemo(() => [
        {
            header: 'Requested Date',
            accessorKey: 'createdAt',
            cell: ({ getValue }) => formatedDateAndTime(getValue()) || '—'
        },
        {
            header: 'Sent From',
            accessorKey: 'from_number',
            cell: ({ getValue }) => getValue() || '—'
        },
        {
            header: 'Sent To',
            accessorKey: 'client_number',
            cell: ({ getValue }) => getValue() || '—'
        },
        {
            header: 'Client Name',
            accessorKey: 'client_name',
            cell: ({ getValue }) => getValue() || '—'
        },
        {
            header: 'Employee Name',
            accessorKey: 'employee_name',
            cell: ({ getValue }) => getValue() || '—'
        },
        {
            header: 'Process Time',
            accessorKey: 'process_time',
            cell: ({ getValue }) => formatedDateAndTime(getValue()) || '—'
        },
        {
            header: 'Delivery Date',
            accessorKey: 'delivered_at',
            cell: ({ getValue }) => formatedDateAndTime(getValue()) || '—'
        },
        {
            header: 'Status',
            accessorKey: 'delivery_status',
            cell: ({ getValue }) => {
                const status = getValue();
                return (
                    <span
                        className="bulk-status-badge"
                        style={{ backgroundColor: DELIVERY_STATUS_COLORS[status] || '#94a3b8' }}
                    >
                        {DELIVERY_STATUS_LABELS[status] || status}
                    </span>
                );
            }
        },
        {
            header: 'Error Message',
            accessorKey: 'error_message',
            cell: ({ getValue }) => {
                const val = getValue();
                return val
                    ? <span className="bulk-error-text" title={val}>{val.length > 40 ? val.slice(0, 40) + '…' : val}</span>
                    : '—';
            }
        }
    ], []);

    // Client-side pagination
    const pageSize = pagination.pageSize;
    const pageIndex = pagination.pageIndex;
    const pageCount = Math.ceil(messages.length / pageSize);
    const pagedMessages = messages.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    return (
        <div className="bulk-report-page">
            <div className="bulk-upload-card">
                {/* Header */}
                <div className="bulk-report-page__header">
                    <div className="bulk-report-page__title-group">
                        <div>
                            <h2>Batch Report</h2>
                            {batch && (
                                <p className="bulk-report-page__subtitle">
                                    {batch.file_name}&nbsp;·&nbsp;{batch.total_records} records
                                </p>
                            )}
                        </div>
                    </div>

                    <button
                        className="bulk-back-btn"
                        onClick={() => navigate('/bulk-upload')}
                    >
                        ← Back
                    </button>
                    {/* <button
                        className="bulk-export-btn"
                        onClick={handleExportCSV}
                        disabled={!batch}
                    >
                        <img src="/download.png" alt="" style={{ width: 16, height: 16 }} />
                        Export CSV
                    </button> */}
                </div>

                <hr />

                {/* Summary pills */}
                {batch && (
                    <div className="bulk-report-page__summary">
                        <span className="bulk-summary-pill bulk-summary-pill--total">
                            Total: {batch.total_records}
                        </span>
                        <span className="bulk-summary-pill bulk-summary-pill--auth">
                            Authenticated: {batch.authenticated_count}
                        </span>
                        <span className="bulk-summary-pill bulk-summary-pill--unauth">
                            Unauthenticated: {batch.unauthenticated_count}
                        </span>
                    </div>
                )}

                {/* Message template */}
                {batch?.original_message && (
                    <div className="bulk-report-page__message-box">
                        <p className="bulk-report-page__message-label">Message Template</p>
                        <p className="bulk-report-page__message-text">{batch.original_message}</p>
                        {batch.attachment_name && (
                            <>
                                <p className="bulk-report-page__message-label">Attachment</p>
                                <p className="bulk-report-page__message-text">
                                    {ATTACHMENT_TYPE_LABELS[batch.attachment_type] || 'File'}: {batch.attachment_name}
                                    {batch.attachment_size ? ` · ${formatFileSize(Number(batch.attachment_size))}` : ''}
                                </p>
                            </>
                        )}
                    </div>
                )}

                {/* Table */}
                <div className="bulk-report-page__body" style={{ position: 'relative' }}>
                    {showLoader && <Loader />}
                    {!showLoader && (
                        <ReactTable
                            columns={columns}
                            data={pagedMessages}
                            pageCount={pageCount}
                            paginationState={{ pagination, setPagination }}
                            totalRecords={messages.length}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkReportPage;
